import { DocItems, DocumentType } from './document';
import { ProductInfo } from './product';
import { EnrichedDocItem } from './enriched-doc-item';
import { round1, round2 } from './round';

/**
 * Merges enriched backend data into an existing DocItems array in-place.
 * Called once after getDocItemsEnriched() resolves, replacing all per-row HTTP calls.
 *
 * @param docItems   - raw DocItems[] returned by getDocItemsByDoc()
 * @param enriched   - EnrichedDocItem[] returned by getDocItemsEnriched()
 * @param vatValue   - fallback VAT rate from globalSettingsData when PRODVAT is null
 * @param docRateVal - exchange rate (DOCRATEVAL) used to convert per-depot income
 *                     prices from foreign currency to local currency, matching the
 *                     single-row path in pullDiRowStockIncomePriceObservable
 */
export function applyEnrichedData(
  docItems: DocItems[],
  enriched: EnrichedDocItem[],
  vatValue: number,
  docRateVal: number = 1
): DocItems[] {

  const map = new Map<number, EnrichedDocItem>(enriched.map(e => [e.DOCITEMID, e]));

  return docItems.map(di => {
    const e = map.get(di.DOCITEMID);
    if (!e) { return di; }

    // VAT normalisation — Invoice (ContractOrder) and Proforma Invoice
    // (CommercialProposal) only. Every other document type (PO/SupplierOrder,
    // receipts, inventory, etc.) prices DOCITEMPRICE/DOCITEMPRICEWOVAT by its
    // own rules and must not have this invoice-specific round-trip applied to
    // it — confirmed 2026-08-20 (owner) after it produced false "currency
    // price mismatch" warnings on PO once PO started calling this function.
    di.PRODVAT = di.PRODVAT ?? vatValue;
    if (di.DOCTYPE === DocumentType.ContractOrder || di.DOCTYPE === DocumentType.CommercialProposal) {
      const vatM = (100.0 + di.PRODVAT) / 100.0;
      di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / vatM);
      di.DOCITEMPRICE      = round2(di.DOCITEMPRICEWOVAT * vatM);
    }

    // Product metadata
    di.PRODOEMNAME     = e.PRODVENDORNAME;
    di.PRODWWWPAGE     = e.PRODWWWPAGE;
    di.PRODBARCODEMASK = e.PRODBARCODEMASK;
    di.npc             = !e.PRODCATEGORY || e.PRODCATEGORY.trim() === '';

    // ACTC
    di.nta = !(di.PRODCUSTCODE === e.ACTC_STRING && e.ACTC_CONFIRMED === 1);

    // Shipped
    di.DOCITEMSHIPPED = e.DOCITEMSHIPPED;

    // prodInfo
    const info = new ProductInfo();
    info.PRODPRICE    = e.PRODPRICE;
    info.PRODINPRICE  = e.PRODINPRICE;
    info.PRODBASEDISC = e.PRODBASEDISC;
    info.PRODQTYDISC  = e.PRODQTYDISC;
    info.PRODDIALDISC = e.PRODDIALDISC;
    info.PRODSTOCK    = e.PRODSTOCK;
    info.PRODSTOCKM   = e.PRODSTOCKM;
    info.PRODSTOCKI   = e.PRODSTOCKI;
    info.PRODSTOCKA   = e.PRODSTOCKA;
    info.PRODRESERVE  = e.PRODRESERVE;
    info.PRODSOLD     = e.PRODSOLD;
    info.PRODONWAY    = e.PRODONWAY;
    info.PRODINPRICEM = e.PRODINPRICEM * docRateVal;
    info.PRODINPRICEI = e.PRODINPRICEI * docRateVal;
    info.PRODINPRICEA = e.PRODINPRICEA * docRateVal;
    info.PRODWEIGHT   = e.PRODWEIGHT;
    info.VENDORPRICE  = e.VENDORPRICE;
    info.VENDORNAME   = e.PRODSUPPLIERNAME ?? undefined;   // default supplier; PRODOEMNAME above is the manufacturer
    di.prodInfo = info;

    di.DOCITEMGLOBALSTOCK = e.PRODSTOCK;

    // Retail price
    di.DOCITEMRETVPRICE = e.DOCITEMRETVPRICE;
    di.DOCITEMRETPRICE  = e.DOCITEMRETPRICE;

    return di;
  });
}
